import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Shuffle, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { FruitCard } from "../components/FruitCard";
import { fetchStats, fetchFeatured, fetchRandom } from "../lib/api";
import { CATEGORIES } from "../lib/config";

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [randomLoading, setRandomLoading] = useState(false);

  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: fetchStats });
  const { data: featured } = useQuery({ queryKey: ["featured"], queryFn: fetchFeatured });

  useEffect(() => {
    document.title = "Fruttipedia | Enciclopedia dei Frutti del Diavolo di One Piece";
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/catalogo?q=${encodeURIComponent(q.trim())}` : "/catalogo");
  };

  const onRandom = async () => {
    setRandomLoading(true);
    try {
      const f = await fetchRandom(true);
      navigate(`/frutto/${f.slug}`);
    } finally {
      setRandomLoading(false);
    }
  };

  const total = stats?.totale;
  const nonCanon = stats?.non_canonici;

  return (
    <div className="App">
      <Header />

      {/* A. Apertura compatta */}
      <section className="relative overflow-hidden" data-testid="home-hero"
        style={{ background: "radial-gradient(ellipse at 88% -10%, #ece5fc 0, transparent 52%)" }}>
        <div className="w-[min(1160px,calc(100%-32px))] mx-auto pt-10 sm:pt-14 pb-8">
          <div className="max-w-2xl">
            <span className="font-bold" style={{ fontSize: 12, letterSpacing: ".13em", textTransform: "uppercase", color: "var(--luma-action)" }}>
              Enciclopedia Lumacofono
            </span>
            <h1 className="font-display leading-[1.1] mt-3 text-3xl sm:text-5xl lg:text-6xl luma-ink">
              Ogni potere ha una storia. <span className="luma-gradient">Scopri la sua.</span>
            </h1>
            <p className="luma-muted mt-4 text-base sm:text-lg max-w-xl">
              L'enciclopedia dei Frutti del Diavolo di One Piece: categorie, possessori, poteri
              e fonti ufficiali, raccolti e verificati scheda per scheda.
            </p>

            <form onSubmit={onSearch} className="mt-6 flex flex-col sm:flex-row gap-2.5 max-w-xl" data-testid="home-search-form">
              <div className="relative flex-1">
                <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 luma-muted" />
                <input
                  data-testid="home-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cerca un frutto o un possessore"
                  className="luma-input w-full pl-12 pr-4 py-3.5 text-base font-medium"
                />
              </div>
              <button type="submit" data-testid="home-search-submit" className="pill-primary px-6 py-3.5 flex items-center justify-center gap-2">
                Cerca <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>
      </section>

      <main className="w-[min(1160px,calc(100%-32px))] mx-auto">
        {/* B. Scoperta casuale */}
        <section className="py-7" data-testid="home-random">
          <div className="luma-card p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
            style={{ background: "linear-gradient(100deg, #F5EEFF, #fff 70%)" }}>
            <div className="flex items-start gap-3">
              <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 float-slow"
                style={{ background: "var(--luma-soft-purple)", color: "var(--luma-action)" }}>
                <Sparkles size={22} />
              </span>
              <div>
                <h2 className="font-display text-xl luma-ink">Che frutto ti capita?</h2>
                <p className="luma-muted text-sm mt-0.5">Lasciati sorprendere da un frutto canonico scelto a caso.</p>
              </div>
            </div>
            <button onClick={onRandom} disabled={randomLoading} data-testid="home-random-btn"
              className="pill-primary px-6 py-3 flex items-center justify-center gap-2 shrink-0">
              {randomLoading ? <Loader2 size={18} className="animate-spin" /> : <Shuffle size={18} />}
              Scopri un frutto
            </button>
          </div>
        </section>

        {/* C. Esplorazione per categoria */}
        <section className="py-4" data-testid="home-categories">
          <div className="flex items-end justify-between mb-5 gap-4">
            <h2 className="font-display text-2xl sm:text-3xl luma-ink">Esplora per categoria</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {CATEGORIES.map((c) => (
              <button key={c.value} onClick={() => navigate(`/catalogo?categoria=${encodeURIComponent(c.value)}`)}
                data-testid={`home-category-${c.value}`}
                className="luma-card luma-card-hover p-6 text-left flex flex-col items-start">
                <span className="w-12 h-12 rounded-2xl mb-4 flex items-center justify-center font-display text-white text-xl"
                  style={{ background: `linear-gradient(140deg, ${c.color}, ${c.color}cc)`, boxShadow: `0 10px 22px -10px ${c.color}aa` }}>
                  {c.label[0]}
                </span>
                <h3 className="font-display text-xl luma-ink">{c.label}</h3>
                <p className="luma-muted text-sm mt-2 flex-1">{c.desc}</p>
                <span className="mt-4 text-sm font-bold flex items-center gap-1.5" style={{ color: "var(--luma-action)" }}>
                  Sfoglia i frutti <ArrowRight size={15} />
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* D. Da scoprire */}
        <section className="py-7" data-testid="home-featured">
          <div className="flex items-end justify-between mb-5 gap-4">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl luma-ink">Da scoprire</h2>
              <p className="luma-muted text-sm mt-1">Una selezione di frutti canonici fra le categorie.</p>
            </div>
          </div>
          {!featured ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin luma-muted" size={28} /></div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {featured.map((f) => <FruitCard key={f.slug} fruit={f} />)}
            </div>
          )}
        </section>

        {/* E. Accesso al catalogo */}
        <section className="pb-4" data-testid="home-catalog-access">
          <div className="luma-card p-7 sm:p-9 text-center" style={{ background: "linear-gradient(180deg, #fff, #F7FBFF)" }}>
            <h2 className="font-display text-2xl sm:text-3xl luma-ink">Vuoi vedere tutto l'archivio?</h2>
            <p className="luma-muted mt-2 max-w-xl mx-auto">
              Cerca, filtra e ordina ogni scheda disponibile su Fruttipedia.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center">
              <button onClick={() => navigate("/catalogo")} data-testid="home-explore-all-btn"
                className="pill-primary px-7 py-3.5 flex items-center justify-center gap-2">
                Esplora tutti i {total ?? "…"} frutti <ArrowRight size={18} />
              </button>
              <button onClick={() => navigate("/catalogo?canonicita=Non%20canonico")} data-testid="home-noncanon-btn"
                className="pill-soft px-6 py-3.5 flex items-center justify-center gap-2">
                Frutti non canonici{nonCanon != null ? ` (${nonCanon})` : ""}
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
